import React from 'react';
import {Link} from 'react-router-dom';
import {faIdCard} from '@fortawesome/free-solid-svg-icons/faIdCard';

import AdminLayout from 'Pages/Admin/AdminLayout';
import {Loading, Pagination} from 'Components/Partials';
import {Pill, plural, joinNames} from 'Components/HumanResources/StaffFolder/Ui';

import User from 'Services/User';
import AdminUsersApi from 'Services/Api/Admin/HumanResources/Users';

/**
 * The staff list. Each row opens that person's staff folder.
 */
export default class UsersIndex extends React.Component {
    /**
     * @var state
     */
    state = {
        loading: true,
        users: null,
        page: 1,
        search: '',
        service_id: '',
        status: 'active',
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.load();
    };

    /**
     * @method load
     */
    load = async () => {
        const {page, search, service_id, status} = this.state;

        this.setState({loading: true});

        const response = await AdminUsersApi.get(null, {
            page,
            per_page: 20,
            with_summary: 'true',
            search: search || null,
            service_id: service_id || null,
            is_archive: status === 'archived' ? 'true' : null,
        });

        this.setState({loading: false, users: response.success ? response.data : null});
    };

    /**
     * @method setFilter
     * @param {string} key
     * @param {string} value
     */
    setFilter = (key, value) => {
        this.setState({[key]: value, page: 1}, () => {
            clearTimeout(this.timer);
            this.timer = setTimeout(this.load, key === 'search' ? 400 : 0);
        });
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {loading, users, page, search, service_id, status} = this.state;
        const services = User.data?.service_access?.enabled ? User.data.service_access.services : [];
        const meta = users?.meta;
        const field = 'w-full rounded-xl border-2 border-gray-300 py-2 px-3 bg-white';

        return (
            <AdminLayout title="Staff" titleIcon={faIdCard}>
                <div className="max-w-6xl space-y-5">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <p className="text-gray-700">
                            {meta ? plural(meta.total, 'staff member', 'staff') + '. ' : ''}
                            Open a folder to see holiday, HR details, forms, rates and timesheets.
                        </p>
                        <Link to="/admin/users/store" className="bg-app-supporting-yellow text-white font-bold rounded-full px-4 py-2">
                            Add staff member
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex flex-col gap-1">
                            <label htmlFor="staff-search" className="text-sm font-bold text-gray-600">Search staff</label>
                            <input
                                id="staff-search"
                                type="search"
                                placeholder="Name or email"
                                className={field}
                                value={search}
                                onChange={(event) => this.setFilter('search', event.target.value)}
                            />
                        </div>
                        {services.length > 0 && (
                            <div className="flex flex-col gap-1">
                                <label htmlFor="staff-service" className="text-sm font-bold text-gray-600">Service</label>
                                <select id="staff-service" className={field} value={service_id} onChange={(event) => this.setFilter('service_id', event.target.value)}>
                                    <option value="">All services</option>
                                    {services.map(service => <option key={service.id} value={service.id}>{service.name}</option>)}
                                </select>
                            </div>
                        )}
                        <div className="flex flex-col gap-1">
                            <label htmlFor="staff-status" className="text-sm font-bold text-gray-600">Status</label>
                            <select id="staff-status" className={field} value={status} onChange={(event) => this.setFilter('status', event.target.value)}>
                                <option value="active">Active</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b-2 border-gray-200">
                                    {['Name', services.length ? 'Services' : null, 'Holiday left', 'Forms', 'Timesheets', '']
                                        .filter(heading => heading !== null)
                                        .map((heading, i) => (
                                            <th key={i} scope="col" className="py-3 px-4 font-bold text-gray-600 whitespace-nowrap">{heading}</th>
                                        ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan={6} className="py-6"><Loading/></td></tr>
                                )}
                                {!loading && users?.data?.length === 0 && (
                                    <tr><td colSpan={6} className="py-6 px-4 text-gray-600">No staff match these filters.</td></tr>
                                )}
                                {!loading && users?.data?.map(person => {
                                    const summary = person.folder_summary ?? {};
                                    const folder = `/admin/human-resources/staff/${person.id}`;

                                    return (
                                        <tr key={person.id} className="border-b border-gray-100 hover:bg-blue-50">
                                            <td className="py-3 px-4">
                                                <Link to={folder} className="flex items-center gap-3">
                                                    <span aria-hidden="true" className="flex-none w-10 h-10 rounded-full bg-app-leading text-white font-bold flex items-center justify-center">
                                                        {(person.first_name?.[0] ?? '') + (person.last_name?.[0] ?? '')}
                                                    </span>
                                                    <span>
                                                        <span className="block font-bold">{person.full_name}</span>
                                                        {person.job_title && <span className="block text-gray-600">{person.job_title}</span>}
                                                    </span>
                                                </Link>
                                            </td>
                                            {services.length > 0 && (
                                                <td className="py-3 px-4">
                                                    {person.services?.length === services.length ? 'All services' : joinNames((person.services ?? []).map(service => service.name)) || '—'}
                                                </td>
                                            )}
                                            <td className="py-3 px-4 whitespace-nowrap">
                                                {summary.holiday_allowance ? plural(summary.holiday_remaining, 'day') : '—'}
                                            </td>
                                            <td className="py-3 px-4">
                                                {summary.forms_outstanding > 0 ?
                                                    <Pill tone="bad">{summary.forms_outstanding} outstanding</Pill> :
                                                    <Pill tone="good">All complete</Pill>}
                                            </td>
                                            <td className="py-3 px-4">
                                                {summary.timesheets_to_review > 0 ?
                                                    <Pill tone="warn">{summary.timesheets_to_review} to review</Pill> :
                                                    <Pill tone="good">Up to date</Pill>}
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <Link to={folder} className="font-bold text-app-leading hover:underline">Open</Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {meta && (
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <span className="text-sm text-gray-600">
                                {meta.total > 0 ? `Showing ${meta.from} to ${meta.to} of ${plural(meta.total, 'staff member', 'staff')}` : ''}
                            </span>
                            {meta.last_page > 1 && (
                                <Pagination page={page} pageCount={meta.last_page} goToPage={(next) => this.setState({page: next}, this.load)} />
                            )}
                        </div>
                    )}
                </div>
            </AdminLayout>
        );
    }
}
